import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';

const API_BASE = '';

export default function LeagueStandings({ leagueId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (!leagueId) return;
    fetch(`${API_BASE}/api/standings/${leagueId}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [leagueId]);

  if (loading || !data?.standings?.length) return null;

  return (
    <div className="mb-6" data-testid="league-standings">
      <h2 className="text-lg font-bold text-slate-200 mb-3 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
        <Trophy className="w-5 h-5 text-amber-400" />
        {t('standings.title', 'Standings')}
      </h2>
      <div className="overflow-x-auto border border-slate-800 rounded-xl">
        <table className="w-full text-xs" data-testid="standings-table">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/50">
              <th className="py-2.5 px-2 text-left text-slate-500 font-medium w-8">#</th>
              <th className="py-2.5 px-2 text-left text-slate-500 font-medium">{t('standings.team', 'Team')}</th>
              <th className="py-2.5 px-2 text-center text-slate-500 font-medium w-8">{t('standings.p', 'P')}</th>
              <th className="py-2.5 px-2 text-center text-slate-500 font-medium w-8">{t('standings.w', 'W')}</th>
              <th className="py-2.5 px-2 text-center text-slate-500 font-medium w-8">{t('standings.d', 'D')}</th>
              <th className="py-2.5 px-2 text-center text-slate-500 font-medium w-8">{t('standings.l', 'L')}</th>
              <th className="py-2.5 px-2 text-center text-slate-500 font-medium hidden sm:table-cell">{t('standings.gf', 'GF')}</th>
              <th className="py-2.5 px-2 text-center text-slate-500 font-medium hidden sm:table-cell">{t('standings.ga', 'GA')}</th>
              <th className="py-2.5 px-2 text-center text-slate-500 font-medium w-8">{t('standings.gd', 'GD')}</th>
              <th className="py-2.5 px-2 text-center text-slate-500 font-medium w-10">{t('standings.pts', 'Pts')}</th>
              <th className="py-2.5 px-2 text-center text-slate-500 font-medium hidden md:table-cell">{t('standings.form', 'Form')}</th>
            </tr>
          </thead>
          <tbody>
            {data.standings.map((row, i) => (
              <tr key={i} className={`border-b border-slate-800/50 hover:bg-slate-800/30 ${i < 4 ? 'bg-emerald-500/3' : ''}`}>
                <td className="py-2 px-2 text-slate-500 font-medium">{row.rank}</td>
                <td className="py-2 px-2">
                  <div className="flex items-center gap-2">
                    {row.team_logo && <img src={row.team_logo} alt="" className="w-4 h-4" loading="lazy" />}
                    <span className="text-slate-200 font-medium truncate max-w-[120px] sm:max-w-[180px]">{row.team_name}</span>
                  </div>
                </td>
                <td className="py-2 px-2 text-center text-slate-400">{row.played}</td>
                <td className="py-2 px-2 text-center text-slate-400">{row.won}</td>
                <td className="py-2 px-2 text-center text-slate-400">{row.drawn}</td>
                <td className="py-2 px-2 text-center text-slate-400">{row.lost}</td>
                <td className="py-2 px-2 text-center text-slate-400 hidden sm:table-cell">{row.goals_for}</td>
                <td className="py-2 px-2 text-center text-slate-400 hidden sm:table-cell">{row.goals_against}</td>
                <td className="py-2 px-2 text-center text-slate-400">{row.goal_diff > 0 ? `+${row.goal_diff}` : row.goal_diff}</td>
                <td className="py-2 px-2 text-center text-slate-100 font-bold">{row.points}</td>
                <td className="py-2 px-2 text-center hidden md:table-cell">
                  {row.form && <FormBadges form={row.form} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FormBadges({ form }) {
  if (!form) return null;
  return (
    <div className="flex gap-0.5 justify-center">
      {form.split('').slice(-5).map((r, i) => (
        <span key={i} className={`w-4 h-4 rounded-sm text-[9px] flex items-center justify-center font-bold ${
          r === 'W' ? 'bg-emerald-500 text-white' :
          r === 'D' ? 'bg-slate-500 text-white' :
          r === 'L' ? 'bg-red-500 text-white' : 'bg-slate-700 text-slate-400'
        }`}>
          {r}
        </span>
      ))}
    </div>
  );
}
